import { useEffect, useState } from "react";
import { App, Button, Card, Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { LogDto } from "@audit-plan/shared";
import { ApiError, download } from "../../api/client";
import { fetchLog } from "../../api/admin";
import { Pill, fmtDT } from "../pills";
import { ResponsiveList } from "../ResponsiveList";
import { Sensitive } from "../Sensitive";

const PAGE = 100;

export function LogTab() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [rows, setRows] = useState<LogDto[]>([]);
  const [more, setMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [entity, setEntity] = useState<string>();
  const [act, setAct] = useState<string>();

  const load = async (reset: boolean) => {
    setLoading(true);
    try {
      const page = await fetchLog({ entity, limit: PAGE, before: reset ? undefined : rows[rows.length - 1]?.ts });
      setRows((r) => (reset ? page : [...r, ...page]));
      setMore(page.length >= PAGE);
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  const ents = [...new Set(rows.map((r) => r.entity))];
  const acts = [...new Set(rows.map((r) => r.action))];
  const shown = rows.filter((r) => !act || r.action === act);

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
        <Select allowClear placeholder={t("a_entity")} style={{ width: 180, maxWidth: "100%" }} value={entity} onChange={setEntity} options={ents.map((e) => ({ value: e, label: e }))} />
        <Select allowClear placeholder={t("operation")} style={{ width: 180, maxWidth: "100%" }} value={act} onChange={setAct} options={acts.map((e) => ({ value: e, label: e }))} />
        <Button icon={<DownloadOutlined />} onClick={() => download("/exports/log.xlsx")}>{t("exportXlsx")}</Button>
      </div>
      <Card styles={{ body: { padding: 0 } }}>
        <ResponsiveList<LogDto>
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={shown}
          pageSize={false}
          emptyText={t("none")}
          card={{
            code: (r) => fmtDT(r.ts),
            title: (r) => <><Sensitive>{r.userName}</Sensitive> · {t(`role_${r.role}`, { defaultValue: r.role })}</>,
            // il dettaglio può contenere email (login, invii): sempre marcato
            subtitle: (r) => <Sensitive>{r.detail}</Sensitive>,
            tags: (r) => (
              <>
                <Pill tone={r.action === "download" ? "info" : "mute"}>{r.action}</Pill>
                <Pill tone="mute">{r.entity}</Pill>
              </>
            ),
          }}
          columns={[
            { title: t("date"), dataIndex: "ts", render: (v: string) => <span style={{ whiteSpace: "nowrap" }}>{fmtDT(v)}</span> },
            { title: t("user"), render: (_, r) => <span><Sensitive>{r.userName}</Sensitive><div style={{ fontSize: 12, opacity: 0.6 }}>{t(`role_${r.role}`, { defaultValue: r.role })}</div></span> },
            { title: t("operation"), dataIndex: "action", render: (v: string) => <Pill tone={v === "download" ? "info" : "mute"}>{v}</Pill> },
            { title: t("a_entity"), dataIndex: "entity" },
            { title: t("detail"), dataIndex: "detail", render: (v: string | null) => <Sensitive>{v}</Sensitive> },
          ]}
        />
      </Card>
      <div style={{ textAlign: "center", marginTop: 12 }}>
        {more ? <Button loading={loading} onClick={() => void load(false)}>{t("a_loadMore")}</Button> : <span style={{ opacity: 0.6 }}>{rows.length} {t("a_rows")}</span>}
      </div>
    </div>
  );
}
